/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Morse parameter packs for metallic crystals — the data behind the
 * 'morse' (metallic) force field in crystalForceFields.ts.
 *
 * Morse is a pairwise stand-in for the proper many-body EAM/Finnis-
 * Sinclair physics that metals really want. It gets cohesion and
 * lattice constant right when fit to them, but cannot reproduce the
 * Cauchy-relation-breaking elastic anisotropy of real metals. That is
 * the EAM regime — a future ManyBodyPotential interface, not a Morse
 * reparameterization.
 *
 * The 'morse' force field `appliesTo` only the compositions registered
 * here AND only metallic crystals (`crystal.metallic` true); every
 * other crystal falls back to the generic LJ force field. Standard
 * source for the elemental-FCC metals is Girifalco & Weizer (1959).
 */
import { CrystalState } from '../../types.ts';
import { MorsePair } from './PairPotential.ts';

export interface MetallicPack {
  /** Literature source for the parameter set. */
  source: string;
  /** Morse parameters keyed by element pair, e.g. 'Cu-Cu'. */
  pairs: Record<string, MorsePair>;
}

/**
 * Registry keyed by composition — the sorted, comma-joined set of
 * element symbols in the crystal basis ('Cu', 'Fe', 'Cu,Au', …).
 * Elemental metals have a single-element key; intermetallics combine.
 *
 * Seeded with Cu (Girifalco-Weizer). Adding an elemental FCC/BCC/HCP
 * metal is one literature-sourced entry per composition. Candidate
 * next entries:
 *   - the other Girifalco-Weizer elementals: Ni, Ag, Au, Pt, Pd, Al,
 *     Pb, Fe (BCC fit), …
 *   - intermetallics (Cu₃Au, Ni₃Al, β-brass, NiAl) need three pairs
 *     each — Morse approximates ordering energies poorly here; EAM
 *     alloy fits (Mishin, Zhou) are the proper treatment.
 */
export const METALLIC_PACKS: Record<string, MetallicPack> = {
  // Cu — Girifalco & Weizer, Phys. Rev. 114 (1959) 687. The canonical
  // Morse parameters for FCC copper; fit to reproduce the cohesive
  // energy (−3.50 eV/atom) and lattice constant (3.615 Å) via a
  // many-shell pair sum (so r₀ here is the SINGLE-PAIR equilibrium,
  // NOT the Cu-Cu nearest-neighbor distance of 2.556 Å in FCC).
  Cu: {
    source: 'Girifalco & Weizer (1959), Morse potential for FCC Cu',
    pairs: {
      'Cu-Cu': { D: 0.3429, alpha: 1.3588, r0: 2.866 },
    },
  },
};

/** Composition key for a crystal: sorted unique basis element symbols. */
const compositionKey = (crystal: CrystalState): string =>
  [...new Set(crystal.basis.map((b) => b.element))].sort().join(',');

/** The Morse pack for `crystal`, or null if none is registered. */
export const metallicPackFor = (crystal: CrystalState): MetallicPack | null =>
  METALLIC_PACKS[compositionKey(crystal)] ?? null;
